/**
 * Device Assign Modal
 * Importera IMEI (klistra in eller .xlsx) och tilldela organisation
 */

import React, { useRef, useState } from 'react';
import { ArrowPathIcon, CheckCircleIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import api from '@/services/api/client';
import { BaseModal } from '@/components/common/BaseModal';
import HierarchicalOrgSelect from '@/components/common/HierarchicalOrgSelect';
import { useTranslation } from '@/store/languageStore';
import { parseDeviceImportXlsx } from '@/utils/deviceImportXlsx';

interface DeviceAssignModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface AssignResult {
  succeeded: string[];
  skipped: string[];
  notFound: string[];
  errors: string[];
  totalInput: number;
  validInput: number;
  invalidInput: number;
}

const DeviceAssignModal: React.FC<DeviceAssignModalProps> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rawInput, setRawInput] = useState('');
  const [imeis, setImeis] = useState<string[]>([]);
  const [organizationId, setOrganizationId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [result, setResult] = useState<AssignResult | null>(null);
  /** Om satt (även 0) skickas till API som registeredCableCount */
  const [registeredCableCount, setRegisteredCableCount] = useState<number | undefined>(undefined);
  const [xlsxError, setXlsxError] = useState<string | null>(null);

  const parseInput = () => {
    const lines = rawInput
      .split(/[\n,;]+/)
      .map((l) => l.trim())
      .filter((l) => l && /^\d{14,16}$/.test(l));
    setImeis([...new Set(lines)]);
    setRegisteredCableCount(undefined);
    setXlsxError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onPickXlsx = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setXlsxError(null);
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) {
      setXlsxError(t('deviceAssignModal.xlsxOnly'));
      e.target.value = '';
      return;
    }
    try {
      const buf = await file.arrayBuffer();
      const { imeis: next, cableCount } = parseDeviceImportXlsx(buf);
      if (next.length === 0) {
        setXlsxError(t('deviceAssignModal.xlsxNoImei'));
        setImeis([]);
        setRegisteredCableCount(undefined);
        setRawInput('');
        return;
      }
      setImeis(next);
      setRawInput('');
      setRegisteredCableCount(cableCount === null ? undefined : cableCount);
    } catch {
      setXlsxError(t('deviceAssignModal.xlsxReadError'));
      setImeis([]);
      setRegisteredCableCount(undefined);
    }
  };

  const handleAssign = async () => {
    if (imeis.length === 0 || !organizationId) return;

    try {
      setAssigning(true);
      const body: {
        imeis: string[];
        organizationId: string;
        registeredCableCount?: number;
      } = { imeis, organizationId };
      if (registeredCableCount !== undefined) {
        body.registeredCableCount = registeredCableCount;
      }
      const response = await api.post('/api/device-management/import/assign-organization', body);

      if (response.data.success) {
        setResult(response.data.data);
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const clearAll = () => {
    setImeis([]);
    setRawInput('');
    setRegisteredCableCount(undefined);
    setXlsxError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <BaseModal
      isOpen={true}
      onClose={result ? onSuccess : onClose}
      title={t('deviceAssignModal.title')}
      subtitle={t('deviceAssignModal.pasteImeiAndSelectOrg')}
      size="2xl"
    >
      {result ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-green-600">
            <CheckCircleIcon className="w-8 h-8" />
            <div>
              <div className="text-lg font-semibold">
                {t('deviceAssignModal.assignmentComplete')}
              </div>
              <div className="text-sm text-textSecondary">
                {result.succeeded.length} {t('deviceAssignModal.devicesAssigned')}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{result.succeeded.length}</div>
              <div className="text-sm text-green-700 dark:text-green-400">
                {t('deviceAssignModal.assigned')}
              </div>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{result.skipped.length}</div>
              <div className="text-sm text-yellow-700 dark:text-yellow-400">
                {t('deviceAssignModal.skippedMounted')}
              </div>
            </div>
            <div className="bg-bgContainer p-3 rounded-lg">
              <div className="text-2xl font-bold text-textSecondary">{result.notFound.length}</div>
              <div className="text-sm text-textSecondary">
                {t('deviceAssignModal.notFound')}
              </div>
            </div>
            {result.invalidInput > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{result.invalidInput}</div>
                <div className="text-sm text-red-700 dark:text-red-400">
                  {t('deviceAssignModal.invalidFormat')}
                </div>
              </div>
            )}
          </div>

          {result.notFound.length > 0 && (
            <div>
              <div className="text-sm font-medium text-textSecondary mb-1">
                {t('deviceAssignModal.notFoundImeis')}
              </div>
              <div className="bg-bgElevated p-2 rounded text-xs font-mono max-h-32 overflow-y-auto">
                {result.notFound.join('\n')}
              </div>
            </div>
          )}

          {result.errors.length > 0 && (
            <div>
              <div className="text-sm font-medium text-red-600 mb-1">
                {t('deviceAssignModal.errors')}
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded text-xs max-h-32 overflow-y-auto text-red-700">
                {result.errors.join('\n')}
              </div>
            </div>
          )}

          <button
            onClick={onSuccess}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('common.close')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-textDefault mb-1">
              {t('common.organization')}
            </label>
            <HierarchicalOrgSelect
              value={organizationId}
              onChange={(id) => setOrganizationId(id || '')}
              placeholder={t('deviceAssignModal.selectOrg')}
              showEmptyOption={false}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-textDefault mb-2">
              {t('deviceAssignModal.xlsxSection')}
            </label>
            <label className="flex flex-col items-center justify-center w-full px-4 py-6 border-2 border-dashed border-borderDefault rounded-lg cursor-pointer hover:bg-bgHover transition-colors">
              <DocumentArrowUpIcon className="w-8 h-8 text-textSecondary mb-1" />
              <span className="text-sm text-textSecondary text-center">{t('deviceAssignModal.xlsxHint')}</span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={onPickXlsx}
              />
            </label>
            {xlsxError && <p className="text-sm text-red-600 mt-1">{xlsxError}</p>}
            <p className="text-xs text-textTertiary mt-1">{t('deviceAssignModal.xlsxImeiNote')}</p>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-borderDefault" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-bgElevated px-2 text-textTertiary">{t('deviceAssignModal.orPaste')}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-textDefault mb-1">
              {t('deviceAssignModal.imeiLabel')}
            </label>
            <textarea
              value={rawInput}
              onChange={(e) => {
                setRawInput(e.target.value);
                setImeis([]);
                setRegisteredCableCount(undefined);
                setXlsxError(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              placeholder={t('deviceAssignModal.pasteImeiHere')}
              rows={8}
              className="w-full px-3 py-2 bg-bgElevated border border-borderDefault rounded-lg text-textDefault font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {imeis.length === 0 ? (
            <button
              onClick={parseInput}
              disabled={!rawInput.trim()}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('deviceAssignModal.validateImei')}
            </button>
          ) : (
            <>
              <div className="bg-bgElevated border border-borderDefault rounded-lg p-3">
                <div className="text-sm text-textSecondary">
                  {t('deviceAssignModal.validImeiFound').replace('{count}', String(imeis.length))}
                </div>
                <div className="mt-2 max-h-32 overflow-y-auto text-xs font-mono text-textTertiary">
                  {imeis.slice(0, 20).join(', ')}
                  {imeis.length > 20 && ` ... +${imeis.length - 20}`}
                </div>
                {registeredCableCount !== undefined && (
                  <div className="mt-2 text-sm text-textDefault border-t border-borderDefault pt-2">
                    {t('deviceAssignModal.cablesFromFile').replace('{n}', String(registeredCableCount))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={clearAll}
                  className="flex-1 px-4 py-2 bg-bgElevated border border-borderDefault text-textDefault rounded-lg hover:bg-bgHover"
                >
                  {t('deviceAssignModal.clear')}
                </button>
                <button
                  type="button"
                  onClick={handleAssign}
                  disabled={assigning || !organizationId}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {assigning && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
                  {t('deviceAssignModal.assignDevices').replace('{count}', String(imeis.length))}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </BaseModal>
  );
};

export default DeviceAssignModal;
